import { useId } from "react";

import { useDialog } from "../interaction";
import { cn } from "../lib/cn";

type SheetSize = "sm" | "md" | "lg";

const sizeClasses: Record<SheetSize, string> = {
  sm: "max-w-sm",
  md: "max-w-xl",
  lg: "max-w-3xl",
};

/*
 * Entry and exit run on the element's own open state, so the paper animates both
 * ways without React holding a "closing" flag: the resting state lives on the
 * `open:` variant, the off state on the bare utilities, and @starting-style
 * (`starting:open:`) gives the browser a from-state on the frame it appears.
 * `display` and `overlay` are in the transition list with allow-discrete, which
 * is what keeps a closing dialog in the top layer until the fade finishes.
 *
 * Tailwind v4 animates `translate`/`scale` as their own properties, not through
 * `transform`, hence both names in the property list.
 */
const sheetMotion = cn(
  "transition-[opacity,translate,scale,overlay,display] transition-discrete duration-200 ease-paper",
  "opacity-0 translate-y-2 scale-[0.98]",
  /* scale-[1] rather than scale-100 so both ends of the transition are plain
     numbers: a number-to-percentage interpolation is not guaranteed to animate. */
  "open:opacity-100 open:translate-y-0 open:scale-[1]",
  "starting:open:opacity-0 starting:open:translate-y-2 starting:open:scale-[0.98]",
  "motion-reduce:transition-none",
);

const backdropMotion = cn(
  "backdrop:bg-desk/50 backdrop:backdrop-blur-[1px]",
  "backdrop:transition-[opacity,display] backdrop:transition-discrete backdrop:duration-200 backdrop:ease-paper",
  "backdrop:opacity-0 open:backdrop:opacity-100 starting:open:backdrop:opacity-0",
  "motion-reduce:backdrop:transition-none",
);

export interface SheetProps extends Omit<
  React.DialogHTMLAttributes<HTMLDialogElement>,
  "open" | "title" | "children" | "onClose" | "onCancel"
> {
  open: boolean;
  /** Called for every user-driven close: Escape, backdrop, the close button. */
  onClose: () => void;
  /** Rendered as the sheet's heading and wired up as its accessible name. */
  title: React.ReactNode;
  children: React.ReactNode;
  /** Actions row on the recessed strip at the bottom. Omit for a bare sheet. */
  footer?: React.ReactNode;
  size?: SheetSize;
  /** When false, Escape and backdrop clicks are ignored and the close button is hidden. */
  dismissable?: boolean;
  className?: string;
  ref?: React.Ref<HTMLDialogElement>;
}

/**
 * The plain held sheet: a bare piece of paper held above the desk, reserved
 * for the few modal surfaces that are not paperwork - a photo held up (the
 * PimsAudit screenshot lightbox). Everything that reads as a form, a confirm
 * or a list to deal with rides the standard modal, Clipboard, at whatever
 * `size` fits. Native `<dialog>`, so it sits in the top layer with no portal
 * and no z-index.
 *
 * Native props land on the `<dialog>` itself. `open` is not one of them: the
 * element's open state is driven imperatively by `useDialog` (showModal, not
 * the attribute), so the prop of that name is the React-side request.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md",
  dismissable = true,
  className,
  ref,
  ...rest
}: SheetProps) {
  const titleId = useId();
  const { dialogRef, handleCancel, handleClose } = useDialog({
    open,
    onClose,
    dismissable,
  });

  return (
    <dialog
      {...rest}
      /* useDialog owns the element, so a caller's ref rides alongside it. */
      ref={(node) => {
        dialogRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      }}
      aria-labelledby={titleId}
      onCancel={handleCancel}
      onClose={handleClose}
      className={cn(
        /* Preflight zeroes the UA's `margin: auto`, so centering is ours to restore.
           Display is `open:flex`, never a base `flex`: an unconditional display
           would override the UA's `dialog:not([open]) { display: none }` and leave
           a closed sheet invisibly in flow. allow-discrete still animates the exit.

           whitespace-normal and text-left: inherited text properties follow the
           dialog into the top layer from wherever its trigger is mounted, so a
           nowrap or right-aligned host would otherwise restyle the sheet's text.
           outline-none: useDialog parks the initial focus on the dialog element
           itself; it is not a Tab stop, so it never needs a ring of its own. */
        "m-auto max-h-[85vh] w-[calc(100vw-2rem)] flex-col whitespace-normal p-0 text-left outline-none open:flex",
        "rounded-sheet border border-rule bg-card font-body text-ink shadow-float",
        sheetMotion,
        backdropMotion,
        sizeClasses[size],
        className,
      )}
    >
      <header className="flex items-start justify-between gap-4 border-b border-rule px-6 pt-5 pb-4">
        <h2
          id={titleId}
          className="font-display text-lg font-medium tracking-tight text-ink"
        >
          {title}
        </h2>
        {dismissable && (
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={cn(
              "-mt-1 -mr-2 inline-flex size-8 shrink-0 items-center justify-center rounded-paper",
              "bg-transparent text-annotation",
              "transition duration-150 ease-paper motion-reduce:transition-none",
              "hover:bg-smudge hover:text-ink",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            )}
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              className="size-4"
            >
              <path d="M4 4 12 12M12 4 4 12" />
            </svg>
          </button>
        )}
      </header>

      {/* min-h-0 is what lets the body shrink inside the flex column and scroll. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 text-ink-body">
        {children}
      </div>

      {footer && (
        <div className="flex justify-end gap-3 rounded-b-sheet border-t border-rule bg-recess px-6 py-4">
          {footer}
        </div>
      )}
    </dialog>
  );
}
